import { ChartTypes, FlamingoResponse } from '@mode/shared/contract-common';
import * as angular from 'angular';

export function isFlamingoServiceError(
  error?: FlamingoResponse.FlamingoErrorResponse
): error is FlamingoResponse.ServiceErrorResponse {
  return error?.type === FlamingoResponse.ResponseType.ServiceError && typeof error.payload?.code === 'string';
}

export function isNoResultsError(error?: FlamingoResponse.FlamingoErrorResponse) {
  return (
    error?.type === FlamingoResponse.ResponseType.DatasetFailed &&
    (error.result?.stateProperties as FlamingoResponse.HelixEmptyDatasetProperties)?.code === 'EMPTY_DATASET'
  );
}

export class EmptyChartStateComponentController implements angular.IComponentController {
  /**
   * @description Optional error binding from component definition
   */
  error?: FlamingoResponse.SelectErrorResponse;

  /**
   * @description Boolean indicating whether or not the error
   * is a Flamingo error we handle explicitly, vs. showing the
   * generic "error with data provided" message
   */
  isHandledFlamingoError = false;
  isNoResultsError = false;
  imageToUse: string;
  chartType: ChartTypes.ChartType;
  chartDescription: string;
  chartRules: string;

  defaultListHTML = `<ul>
    <li>&#8226; 1 field in the x-axis</li>
    <li>&#8226; 1 or more fields in the y-axis</li>
  </ul>`;

  $onChanges(changes: angular.IOnChangesObject) {
    this.setupChartText();

    if (changes.error) {
      this.updateErrorState();
    }
  }

  /**
   * sets up the title and list text for each type of chart when empty
   */
  setupChartText() {
    switch (this.chartType) {
      case 'line':
        this.chartDescription = 'To create a line chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'totalArea':
        this.chartDescription = 'To create a 100% area chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'bar':
        this.chartDescription = 'To create a grouped bar chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'hBar':
        this.chartDescription = 'To create a horizontal grouped bar chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 or more fields in the x-axis</li>
          <li>&#8226; 1 field in the y-axis</li>
        </ul>`;
        break;
      case 'stackedBar':
        this.chartDescription = 'To create a bar chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'stackedBar100':
        this.chartDescription = 'To create a 100% bar chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'hStackedBar':
        this.chartDescription = 'To create a horizontal bar chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 or more fields in the x-axis</li>
          <li>&#8226; 1 field in the y-axis</li>
        </ul>`;
        break;
      case 'hStackedBar100':
        this.chartDescription = 'To create a 100% horizontal bar chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 or more fields in the x-axis</li>
          <li>&#8226; 1 field in the y-axis</li>
        </ul>`;
        break;
      case 'area':
        this.chartDescription = 'To create an area chart, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'scatter':
        this.chartDescription = 'To create a scatter plot, drop:';
        this.chartRules = this.defaultListHTML;
        break;
      case 'linePlusBar':
        this.chartDescription = 'To create a line + bar chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 field in the x-axis</li>
          <li>&#8226; 2 fields in the y-axis</li>
        </ul>`;
        break;
      case 'donut':
        this.chartDescription = 'To create a donut chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 field in color</li>
          <li>&#8226; 1 field in angle</li>
        </ul>`;
        break;
      case 'pie':
        this.chartDescription = 'To create a pie chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 field in color</li>
          <li>&#8226; 1 field in angle</li>
        </ul>`;
        break;
      case 'vegas':
        this.chartDescription = 'To create a visualization, drop:';
        this.chartRules = `<ul>
          <li>&#8226; fields in rows and columns</li>
          <li>&#8226; fields in layers</li>
        </ul>`;
        break;
      case 'table':
        this.chartDescription = 'To create a table, drop fields into columns';
        this.chartRules = `<ul><li>or <a href="#" ng-click="ctrl.onAddAllFields()">add all your fields</a> at once</li></ul>`;
        break;
      case 'bigValue':
        this.chartDescription = 'To create a big value chart, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 field in value</li>
        </ul>`;
        break;
      case 'vegasPivotTable':
        this.chartDescription = 'To create a pivot table, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 or more fields to columns and rows</li>
          <li>&#8226; 1 or more fields to  values</li>
        </ul>`;
        break;
      case 'histogram':
        this.chartDescription = 'To create a histogram, drop:';
        this.chartRules = `<ul>
          <li>&#8226; 1 continuous field in the X-Axis</li>
        </ul>`;
        break;
    }
  }
  /**
   * @description To be called when the error binding updates. Determines
   * if this is an error type + code we handle explicitly, showing a
   * more descriptive error message [in the template] if so
   */
  updateErrorState() {
    this.isHandledFlamingoError = false;
    this.isNoResultsError = false;
    if (isFlamingoServiceError(this.error)) {
      switch (this.error.payload.code) {
        // Handled codes
        case FlamingoResponse.FlamingoErrorCode.SubresultTooLarge:
        case FlamingoResponse.FlamingoErrorCode.DateOutOfRange:
        case FlamingoResponse.FlamingoErrorCode.UnexpectedInfinityOrNaN: {
          this.isHandledFlamingoError = true;
          break;
        }
      }
    }

    if (isNoResultsError(this.error)) {
      this.isNoResultsError = true;
    }
  }
}

export const EmptyChartStateComponent: angular.IComponentOptions = {
  bindings: {
    chartType: '<',
    isValid: '<',
    isComplete: '<',
    onAddAllFields: '&',
    planFailed: '<',
    error: '<?',
  },
  controller: EmptyChartStateComponentController,
  controllerAs: 'ctrl',
  template: `
  <div class="chart-placeholder">
    <div class="empty-chart-state" ng-if="!ctrl.isComplete && ctrl.isValid && !ctrl.isNoResultsError && !ctrl.isHandledFlamingoError">
      <div ng-if="ctrl.chartType === 'table'">
        <capra-icon icon="illo-viz-editor-empty-state" class="empty-state-illo"></capra-icon>
        <p>To create a table, drop fields into columns</p>
        <ul><li>or <a href="#" ng-click="ctrl.onAddAllFields()">add all your fields</a> at once</li></ul>
      </div>
      <div ng-if="ctrl.chartType !== 'table'">
        <capra-icon icon="illo-viz-editor-empty-state" class="empty-state-illo"></capra-icon>
        <p>{{ctrl.chartDescription}}</p>
        <div ng-bind-html="ctrl.chartRules"></div>
      </div>
    </div>
    <div ng-if="ctrl.isHandledFlamingoError" class="chart-error-container">
      <div class="error">
        <div><capra-icon icon="illo-viz-editor-broken-viz" class="broken-viz-illo"></capra-icon></div>
        <!-- FlamingoErrorCode.SubresultTooLarge -->
        <p ng-if="ctrl.error.payload.code === '${FlamingoResponse.FlamingoErrorCode.SubresultTooLarge}'">
          Your visualization can't be displayed because your data is too large&mdash;<br>
          try filtering your data first.
        </p>
        <!-- FlamingoErrorCode.DateOutOfRange -->
        <p ng-if="ctrl.error.payload.code === '${FlamingoResponse.FlamingoErrorCode.DateOutOfRange}'">
          Sorry, this visualization can't be displayed&mdash;<br>
          a field contains dates that fall outside the supported range,<br>years 1583 to 9999 inclusive.
        </p>
        <!-- FlamingoErrorCode.UnexpectedNaN -->
        <p ng-if="ctrl.error.payload.code === '${FlamingoResponse.FlamingoErrorCode.UnexpectedInfinityOrNaN}'">
          Sorry, this visualization can't be displayed&mdash;<br>a field contains Infinity or NaN values.
        </p>
      </div>
    </div>
    <div ng-if="!ctrl.isHandledFlamingoError && ctrl.isNoResultsError" class="chart-error-container">
      <div class="error">
        <p>No results</p>
      </div>
    </div>
    <div ng-if="!ctrl.isHandledFlamingoError && !ctrl.isNoResultsError && (!ctrl.isValid || (ctrl.planFailed && ctrl.isComplete))" class="chart-error-container">
      <div class="error">
        <div><capra-icon icon="illo-viz-editor-broken-viz" class="broken-viz-illo"></capra-icon></div>
        <p>Sorry, this visualization can't be displayed&mdash;<br>there's an error with the data provided.</p>
      </div>
    </div>
  </div>`,
};
